import { useLocalSearchParams } from 'expo-router';
import { OtpSearchParams } from 'features/otp/constants/OtpSearchParams';
import { OtpVerificationType } from 'features/otp/constants/OtpVerificationType';
import useRedirect from 'features/otp/hooks/useRedirection/useRedirection';
import useResendOtp from 'features/otp/hooks/useResendOtp/useResendOtp';
import useVerifyChallenge from 'features/otp/hooks/useVerifyChallenge/useVerifyChallenge';
import OtpScreen from 'features/otp/OtpScreen';
import { DEVICE_VERIFICATION_TYPE } from 'features/signup/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useWhereToGo from 'shared/hooks/useWhereToGo/useWhereToGo';
import { useAuth } from 'shared/providers/auth';

const VERIFY_SCREEN_ERROR_CODES = [3, 9, 11];

const VerifyDevice = () => {
  const { t: translatedTextOtp } = useTranslation('otp');
  const { verifyChallenge, isLoading: isVerifyChallengeLoading } =
    useVerifyChallenge();
  const {
    anonymousId,
    session,
    emailAddress,
    phoneNumber,
    screenType,
    lastFourDigitPhoneNumber,
    flowKey,
    loginMethod,
    usernameType,
    deviceNotificationStatus,
  } = useLocalSearchParams<OtpSearchParams>();
  const { challengeRedirect } = useRedirect();
  const { initiateWhereToGo, isLoading: isWhereToGoFetching } = useWhereToGo();
  const { handleLogout } = useAuth();
  const resendOtp = useResendOtp();

  const handleLogoutWrapper = async () => {
    await handleLogout({
      shouldNavigateToLogin: true,
    });
  };

  useBackHandler(handleLogoutWrapper);

  const signedIn = usePressableAnalytics({
    analyticsId: 'signedIn',
    customAttributes: {
      login_method: loginMethod,
      username_type: usernameType,
      device_notification_status: deviceNotificationStatus,
    },
  });

  const handleVerifyOtp = async (code: string) => {
    const result = await verifyChallenge(
      {
        emailAddress: emailAddress ?? '',
        code: code,
        phoneNumber: phoneNumber ?? '',
        anonymousId: anonymousId ?? '',
        session: session ?? '',
      },
      { verifyType: DEVICE_VERIFICATION_TYPE },
    );

    if (result.data?.challenge) {
      const { challenge, phoneLineNumber } = result.data || {};
      await challengeRedirect(
        challenge || '',
        emailAddress || '',
        phoneLineNumber || phoneNumber || '',
        phoneLineNumber || '',
        result.data?.session || '',
        screenType || '',
      );
    }

    if (result.success) {
      signedIn();
      initiateWhereToGo();
    }

    return result;
  };

  // Handler passed to OtpScreen for resend
  const handleResendOtp = async () => {
    return await resendOtp({
      anonymousId: anonymousId ?? '',
      emailAddress: emailAddress ?? '',
      phoneNumber: phoneNumber ?? '',
    });
  };

  const description = `${translatedTextOtp('deviceVerification.description')}${String(lastFourDigitPhoneNumber)}.`;

  return (
    <OtpScreen
      flowKey={flowKey}
      verificationType={OtpVerificationType.PHONE}
      // Text content
      headerText={translatedTextOtp('deviceVerification.header')}
      description={description}
      // External state
      isLoading={isVerifyChallengeLoading || isWhereToGoFetching}
      // Configurable error codes
      screenLevelErrorCodes={VERIFY_SCREEN_ERROR_CODES}
      // Event handlers
      onVerifyOtp={handleVerifyOtp}
      onResendOtp={handleResendOtp}
      onBack={handleLogoutWrapper}
    />
  );
};

export default VerifyDevice;
